/**
 * Stappen in een Contact Form 7-formulier.
 *
 * De PHP-kant zet alleen markeringen neer op de plekken waar de redacteur een
 * [stap]-tag heeft ingevoegd. Hier knippen we het formulier langs die
 * markeringen op, bouwen we de stappenbalk en de knoppen, en regelen we het
 * heen en weer lopen.
 *
 * Waarom in de browser en niet in PHP: CF7 zet het formulier in één keer in
 * elkaar en past er wpautop op toe. Zou de tag halve <div>'s uitspugen, dan
 * levert dat ongeldige HTML op zodra de redacteur de markering ergens anders
 * neerzet. Zo blijft de uitvoer van CF7 altijd geldig.
 *
 * Alleen actief op formulieren waarvoor de vormgeving is aangezet.
 */

/**
 * Hoe je in de HTML van CF7 ziet dat een veld verplicht is.
 *
 * Niet aan het attribuut `required`: dat zet CF7 nergens neer. Het formulier
 * draait op `novalidate` en de plugin controleert alles pas op de server. Het
 * enige merkteken in de uitvoer is een klasse — `wpcf7-validates-as-required`
 * op het veld, en bij een akkoordvinkje `wpcf7-acceptance`, dat verplicht is
 * tenzij de redacteur er `optional` bij heeft gezet.
 *
 * Die klasse staat op het formulier-element, en dat is bij keuzerondjes en
 * aankruisvakjes het omhullende <span> om de losse vakjes heen en niet de
 * invoervelden zelf. Daarom kijken we per element en niet per <input>.
 */
const VERPLICHT = '[required], .wpcf7-validates-as-required, .wpcf7-acceptance:not(.optional)';

function isIngevuld(element) {
  // Een rondje of vakje dat zélf `required` draagt. Dit moet vóór de controle
  // hieronder staan: een losse input heeft altijd een `value`, ook als er niets
  // is aangekruist, en dan zou elk keuzeveld meteen als ingevuld tellen.
  //
  // Bij een groep rondjes is één aangekruiste genoeg, en `required` staat vaak
  // maar op één van de rondjes. Daarom kijken we naar de hele groep met
  // dezelfde naam en niet naar dit ene element.
  if (element.matches('input[type="radio"], input[type="checkbox"]')) {
    const groep = element.form && element.name
      ? element.form.querySelectorAll(`input[name="${CSS.escape(element.name)}"]`)
      : [element];

    return Array.from(groep).some((vakje) => vakje.checked);
  }

  if (element.matches('input, select, textarea')) {
    return element.value.trim() !== '';
  }

  // Groep vakjes of rondjes: één aangekruiste is genoeg.
  return element.querySelector('input:checked') !== null;
}

function stapIsCompleet(stap) {
  return Array.from(stap.querySelectorAll(VERPLICHT)).every(isIngevuld);
}

function bouwStappen(formulier) {
  const markeringen = Array.from(formulier.querySelectorAll('.cobs-stap-markering'));

  if (!markeringen.length) return; // geen markeringen: laat het formulier met rust

  // De inhoud van het formulier zit in een wrapper die CF7 zelf neerzet.
  const houder = markeringen[0].closest('form');
  if (!houder) return;

  // 1. Alles vanaf een markering tot de volgende in een eigen stap zetten.
  const stappen = [];
  let huidige = null;

  Array.from(houder.childNodes).forEach((knoop) => {
    const isMarkering =
      knoop.nodeType === 1 && knoop.classList && knoop.classList.contains('cobs-stap-markering');

    if (isMarkering) {
      huidige = document.createElement('div');
      huidige.className = 'cobs-stap';
      huidige.dataset.titel = knoop.dataset.titel || '';
      houder.insertBefore(huidige, knoop);
      knoop.remove();
      stappen.push(huidige);
      return;
    }

    // Alles vóór de eerste markering blijft staan waar het staat.
    if (huidige) huidige.appendChild(knoop);
  });

  if (!stappen.length) return;

  // 2. De responsmelding en de voettekst staan achter de laatste markering en
  //    belanden daardoor in de laatste stap. Ze horen bij het formulier als
  //    geheel, dus die halen we er weer uit.
  let voettekst = null;

  stappen.forEach((stap) => {
    stap.querySelectorAll('.wpcf7-response-output').forEach((el) => houder.appendChild(el));

    const tekst = stap.querySelector('.cobs-voettekst');
    if (tekst) voettekst = tekst;
  });

  // 3. Stappenbalk.
  const balk = document.createElement('ol');
  balk.className = 'cobs-stappenbalk';

  // De nummers zijn knoppen: daarmee spring je terug naar een stap die je al
  // hebt gehad. Een stap die nog niet vrij is, staat op `disabled` — zo valt
  // hij ook met het toetsenbord over te slaan en is meteen te zien dat er nog
  // niets te halen valt.
  stappen.forEach((stap, i) => {
    const item = document.createElement('li');
    item.className = 'cobs-stappenbalk__stap';
    item.innerHTML =
      `<button type="button" class="cobs-stappenbalk__nummer" data-stap="${i}">${i + 1}</button>` +
      (stap.dataset.titel ? `<span class="cobs-stappenbalk__titel">${stap.dataset.titel}</span>` : '');
    balk.appendChild(item);
  });

  const nummers = Array.from(balk.querySelectorAll('button.cobs-stappenbalk__nummer'));

  // Extra bolletjes voor stappen die niet in dit formulier zitten maar er wel
  // bij horen, zoals de betaalstap van het aanmeldformulier die pas ná het
  // versturen komt. Komma-gescheiden titels in `data-extra-stappen`. Geen
  // knop: er valt hier niets te kiezen.
  (houder.dataset.extraStappen || '')
    .split(',')
    .map((titel) => titel.trim())
    .filter(Boolean)
    .forEach((titel, j) => {
      const item = document.createElement('li');
      item.className = 'cobs-stappenbalk__stap is-extra';
      item.innerHTML =
        `<span class="cobs-stappenbalk__nummer" aria-hidden="true">${stappen.length + j + 1}</span>` +
        `<span class="cobs-stappenbalk__titel">${titel}</span>`;
      balk.appendChild(item);
    });

  houder.insertBefore(balk, stappen[0]);

  // 4. Knoppenrij. De verzendknop verhuist hierheen en verschijnt alleen bij de
  //    laatste stap. CF7 geeft die knop de klasse `wpcf7-submit`; een gewoon
  //    formulier heeft alleen `type="submit"`. De klasse die de vormgeving
  //    aanspreekt zetten we hier, zodat beide er hetzelfde uitzien.
  const verzend = houder.querySelector('.wpcf7-submit, [type="submit"]');
  if (verzend) verzend.classList.add('cobs-stapnav__verzenden');
  const navigatie = document.createElement('div');
  navigatie.className = 'cobs-stapnav';

  // De terugknop is een gewone themaknop in de donkere omlijnde variant; zo
  // blijft hij gelijk aan de knoppen elders op de site.
  const vorige = document.createElement('button');
  vorige.type = 'button';
  vorige.className = 'cobs-stapnav__vorige btn btn-dark-outline';
  vorige.textContent = 'Vorige';

  const volgende = document.createElement('button');
  volgende.type = 'button';
  volgende.className = 'cobs-stapnav__volgende';
  volgende.textContent = 'Volgende stap';

  navigatie.appendChild(vorige);
  navigatie.appendChild(volgende);
  if (verzend) navigatie.appendChild(verzend);

  // Een voetregel die bij elke stap onderin hoort — het meelopende bedrag van
  // het aanmeldformulier — staat in de HTML achter de laatste markering en is
  // zo in de laatste stap beland. Die verhuist naar de knoppenrij, links van
  // de knoppen. Zonder JavaScript staat hij gewoon onder de laatste stap.
  const voet = houder.querySelector('.cobs-stapvoet');

  if (voet) {
    navigatie.insertBefore(voet, navigatie.firstChild);
    navigatie.classList.add('cobs-stapnav--met-voet');
  }

  stappen[stappen.length - 1].after(navigatie);

  // De voettekst hoort onder de knoppen, boven de responsmelding.
  if (voettekst) navigatie.after(voettekst);

  // 5. Tonen en verbergen.
  let actief = 0;
  let hoogst = 0; // verste stap waar de bezoeker is geweest

  // Een stap is vrijgespeeld als je er al bent geweest én alles ervóór nog
  // steeds is ingevuld. Die tweede voorwaarde is nodig omdat je kunt teruglopen
  // en een veld kunt leegmaken; dan gaat wat daarachter lag weer op slot.
  const isVrij = (index) =>
    index <= hoogst && stappen.slice(0, index).every(stapIsCompleet);

  // De knop naar de volgende stap blijft op slot zolang er in deze stap nog
  // een verplicht veld leeg is. CF7 controleert dat zelf pas bij verzenden, en
  // dan is de bezoeker al drie stappen verder.
  const ijkKnoppen = () => {
    volgende.disabled = !stapIsCompleet(stappen[actief]);

    nummers.forEach((knop, i) => {
      knop.disabled = !isVrij(i);
    });
  };

  // Bij een stapwissel via de knoppen of de balk terug naar de bovenkant van
  // het formulier. Op een lang formulier staat "Volgende stap" onderaan en
  // bleef het beeld daar hangen, met de nieuwe stap buiten beeld erboven. Niet
  // bij de eerste weergave: dan hoort de pagina te blijven waar hij is.
  const scrollNaarBoven = () => {
    houder.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toon = (index, scrollen = false) => {
    // Alleen een bestaande stap. Een ongeldige index zou de huidige stap
    // sluiten en `hoogst` op NaN zetten, waarna geen stap meer vrij is.
    if (!Number.isInteger(index) || index < 0 || index >= stappen.length) return;

    actief = index;
    hoogst = Math.max(hoogst, index);

    stappen.forEach((stap, i) => stap.classList.toggle('is-actief', i === index));

    Array.from(balk.children).forEach((item, i) => {
      item.classList.toggle('is-actief', i === index);
      item.classList.toggle('is-gedaan', i < index);
    });

    vorige.hidden = index === 0;
    volgende.hidden = index === stappen.length - 1;
    if (verzend) verzend.hidden = index !== stappen.length - 1;

    ijkKnoppen();

    if (scrollen) scrollNaarBoven();
  };

  // `input` dekt typen, `change` de keuzevelden en het uploadveld. Beide
  // bubbelen tot het formulier, dus één luisteraar volstaat voor alle stappen.
  houder.addEventListener('input', ijkKnoppen);
  houder.addEventListener('change', ijkKnoppen);

  balk.addEventListener('click', (event) => {
    // Alleen de echte stapknoppen. De extra bolletjes (data-extra-stappen,
    // zoals "Betaling") zijn spans met dezelfde klasse maar zonder stapnummer;
    // een klik daarop gaf toon(NaN): de stap sloot, en na nog een klik was
    // geen enkele stap meer te openen (gemeld 03-11-2026).
    const knop = event.target.closest('button.cobs-stappenbalk__nummer');

    // De meeste browsers laten een klik op een uitgeschakelde knop niet door,
    // maar dat geldt niet overal en niet voor een klik vanuit script. Hier
    // staat de grens, niet in de browser.
    if (knop && !knop.disabled) toon(Number(knop.dataset.stap), true);
  });

  vorige.addEventListener('click', () => toon(Math.max(0, actief - 1), true));

  volgende.addEventListener('click', () => toon(Math.min(stappen.length - 1, actief + 1), true));

  // Na een mislukte verzending terug naar de stap met de eerste fout.
  formulier.addEventListener('wpcf7invalid', () => {
    const fout = houder.querySelector('.wpcf7-not-valid');
    const stap = fout ? fout.closest('.cobs-stap') : null;
    if (stap) toon(stappen.indexOf(stap), true);
  });

  houder.classList.add('cobs-stappen--klaar');

  // Een formulier dat niet via ajax verstuurt, post en herlaadt de pagina; er
  // is dan geen `wpcf7invalid` om op te wachten. De server geeft in dat geval
  // met `data-stap-fout` door waar de eerste fout zat, zodat de bezoeker niet
  // weer op stap 1 begint terwijl de melding drie stappen verderop hoort.
  const startstap = Number(houder.dataset.stapFout);

  toon(Number.isInteger(startstap) && startstap > 0 && startstap < stappen.length ? startstap : 0);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('form.cobs-stappen').forEach(bouwStappen);
});
